import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Pencil, Bot, Trash2, Hash, FileText, History, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, CodeBadge } from "@/components/StatusBadge";
import { CoverImage } from "@/components/CoverImage";
import { SmartlinkCard } from "@/components/SmartlinkCard";
import { SoundlinkCard } from "@/components/SoundlinkCard";
import { PlatformStatus } from "@/components/PlatformStatus";
import { ReleaseAnalytics } from "@/components/analytics/ReleaseAnalytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LANGUAGES } from "@/constants/catalog";
import { t, fmtDateTime } from "@/i18n";

const Meta = ({ label, value }) => (
  <div><p className="eyebrow">{t(label)}</p><p className="text-sm font-medium mt-0.5" data-testid={`meta-${label.toLowerCase().replace(/\W+/g, "-")}`}>{value || "—"}</p></div>
);

const CodesDialog = ({ rel, open, onClose, onSaved }) => {
  const [upc, setUpc] = useState(rel.upc || "");
  const [isrcs, setIsrcs] = useState(Object.fromEntries(rel.tracks.map((t) => [t.id, t.isrc || ""])));
  const save = async () => {
    try {
      const { data } = await api.post(`/releases/${rel.id}/codes`, { upc, isrcs });
      toast.success(t("Códigos asignados")); onSaved(data); onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="codes-dialog">
        <DialogHeader><DialogTitle>{t("Asignar UPC / ISRC manualmente")}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><p className="eyebrow mb-1">UPC</p><Input value={upc} onChange={(e) => setUpc(e.target.value)} className="font-mono" data-testid="codes-upc-input" /></div>
          {rel.tracks.map((t) => (
            <div key={t.id}><p className="eyebrow mb-1 truncate">ISRC · {t.title}</p><Input value={isrcs[t.id]} onChange={(e) => setIsrcs({ ...isrcs, [t.id]: e.target.value.toUpperCase() })} className="font-mono" data-testid={`codes-isrc-input-${t.track_number}`} /></div>
          ))}
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>{t("Cancelar")}</Button><Button onClick={save} data-testid="codes-save-button">{t("Guardar")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function ReleaseDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [rel, setRel] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [codesOpen, setCodesOpen] = useState(false);

  const load = () => { api.get(`/releases/${id}`).then((r) => setRel(r.data)).catch(() => navigate("/lanzamientos")); if (can("admin", "manager")) api.get("/jobs", { params: { release_id: id } }).then((r) => setJobs(r.data)); };
  useEffect(load, [id]); // eslint-disable-line
  useEffect(() => { if (!rel || !["queued", "uploading", "waiting_2fa"].includes(rel.status)) return; const t = setInterval(load, 3000); return () => clearInterval(t); }, [rel?.status]); // eslint-disable-line

  if (!rel) return <p className="text-muted-foreground text-sm">{t("Cargando…")}</p>;
  const busy = ["queued", "uploading", "waiting_2fa"].includes(rel.status);
  const staff = can("admin", "manager");
  const trigger = async () => { try { const { data } = await api.post("/jobs", { release_id: id }); toast.success(t("Bot iniciado")); navigate(`/bot/${data.id}`); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async () => { if (!window.confirm(t("¿Eliminar este lanzamiento?"))) return; try { await api.delete(`/releases/${id}`); toast.success(t("Eliminado")); navigate("/lanzamientos"); } catch (e) { toast.error(errMsg(e)); } };
  const lang = (c) => t(LANGUAGES.find((l) => l[0] === c)?.[1] || c);

  return (
    <div className="space-y-8" data-testid="release-detail">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4 space-y-4">
          <CoverImage fileId={rel.cover_file_id} className="w-full aspect-square rise" />
          <SmartlinkCard release={rel} onUpdated={load} />
          <SoundlinkCard releaseId={rel.id} />
          {rel.contract_file_id && <a href={fileUrl(rel.contract_file_id)} target="_blank" rel="noreferrer" className="text-sm text-primary flex items-center gap-2 hover:underline" data-testid="contract-link"><FileText className="h-4 w-4" /> {rel.contract_filename || t("Contrato")}</a>}
        </div>
        <div className="lg:col-span-8 space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-1"><p className="eyebrow">{rel.release_type} · {rel.label}</p><StatusBadge status={rel.status} /></div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight truncate" data-testid="release-title">{rel.title}</h1>
              <p className="text-lg text-muted-foreground">{rel.artist_name}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" onClick={() => navigate(`/lanzamientos/${id}/editar`)} disabled={busy} data-testid="btn-edit-release" className="gap-2"><Pencil className="h-4 w-4" /> {t("Editar")}</Button>
              {can("admin", "manager") && <>
                <Button variant="outline" onClick={() => setCodesOpen(true)} data-testid="btn-assign-codes" className="gap-2"><Hash className="h-4 w-4" /> {t("Códigos")}</Button>
                <Button onClick={trigger} disabled={busy || rel.status === "approved"} data-testid="btn-trigger-downtown-automation" className="gap-2"><Bot className="h-4 w-4" /> {busy ? t("Bot en proceso") : rel.status === "completed" ? t("Reenviar a Downtown") : t("Enviar a Downtown")}</Button>
              </>}
              {can("admin") && <Button variant="ghost" onClick={remove} title={t("Eliminar lanzamiento (solo administradores)")} data-testid="btn-delete-release" className="text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>}
            </div>
          </div>
          <div className="panel p-6 grid grid-cols-2 md:grid-cols-4 gap-5">
            <div><p className="eyebrow">UPC</p><div className="mt-1"><CodeBadge code={rel.upc} kind="upc" /></div></div>
            <Meta label="Live date" value={rel.live_date} />
            <Meta label="Género" value={[rel.primary_genre, rel.secondary_genre].filter(Boolean).join(" / ")} />
            <Meta label="Idioma" value={lang(rel.language)} />
            <Meta label="Territorios" value={rel.territories.mode === "worldwide" ? t("Mundial") : t("{n} países", { n: rel.territories.countries.length })} />
            <Meta label="Tiendas" value={t("{n} DSP", { n: rel.stores.length })} />
            <Meta label="P-Line" value={rel.p_line} />
            <Meta label="C-Line" value={rel.c_line} />
          </div>
          {rel.stores.length > 0 && <div className="flex flex-wrap gap-1.5">{rel.stores.map((s) => <span key={s} className="text-xs rounded-full border border-border px-2.5 py-0.5 text-muted-foreground">{s}</span>)}</div>}
          {staff && rel.downtown_url && <a href={rel.downtown_url} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1.5" data-testid="downtown-album-link"><ExternalLink className="h-3.5 w-3.5" /> {t("Ver álbum #{id} en Downtown", { id: rel.downtown_release_id })}</a>}
        </div>
      </div>

      <PlatformStatus rel={rel} onUpdated={setRel} />

      <ReleaseAnalytics releaseId={rel.id} />

      <section className="panel overflow-hidden rise">
        <div className="px-6 pt-5 pb-3 flex items-center justify-between"><h2 className="font-display text-lg font-semibold">Tracks</h2><span className="text-xs text-muted-foreground">{t("{a}/{b} con ISRC", { a: rel.tracks.filter((x) => x.isrc).length, b: rel.tracks.length })}</span></div>
        <Table data-testid="tracks-table">
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead className="w-10">#</TableHead><TableHead>{t("Título")}</TableHead><TableHead>{t("Artistas")}</TableHead><TableHead>ISRC</TableHead><TableHead>{t("Idioma")}</TableHead><TableHead>{t("Explícito")}</TableHead><TableHead>{t("Audio")}</TableHead></TableRow></TableHeader>
          <TableBody>
            {rel.tracks.map((tr) => (
              <TableRow key={tr.id} data-testid={`track-row-${tr.track_number}`}>
                <TableCell className="font-mono text-xs text-muted-foreground">{String(tr.track_number).padStart(2, "0")}</TableCell>
                <TableCell><p className="font-medium">{tr.title}</p>{tr.version && <p className="text-xs text-muted-foreground">{tr.version}</p>}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{tr.artists.map((a) => a.name).join(", ") || "—"}</TableCell>
                <TableCell><CodeBadge code={tr.isrc} kind="isrc" /></TableCell>
                <TableCell className="text-sm">{lang(tr.audio_language)}</TableCell>
                <TableCell className="text-sm">{tr.explicit ? <span className="text-amber-500 font-medium">{t("Sí")}</span> : t("No")}</TableCell>
                <TableCell className="text-xs">{tr.audio_file_id ? <a href={fileUrl(tr.audio_file_id)} target="_blank" rel="noreferrer" className="text-primary hover:underline">{tr.audio_filename || "audio"}</a> : <span className="text-muted-foreground italic">{t("sin audio")}</span>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </section>

      {staff && <section className="panel p-6 rise">
        <h2 className="font-display text-lg font-semibold mb-3 flex items-center gap-2"><History className="h-4 w-4" /> {t("Historial del bot")}</h2>
        {jobs.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="jobs-empty">{t("Este lanzamiento aún no se ha enviado a Downtown.")}</p> : (
          <ul className="divide-y divide-border">
            {jobs.map((j) => (
              <li key={j.id} className="py-2.5 flex items-center justify-between gap-4 text-sm">
                <Link to={`/bot/${j.id}`} className="hover:text-primary truncate" data-testid={`job-link-${j.id}`}>{fmtDateTime(j.created_at)} · {t(j.mode === "simulation" ? "simulación" : "real")} · {t("por {name}", { name: j.triggered_by })}</Link>
                <div className="flex items-center gap-3">{j.error && <span className="text-xs text-destructive truncate max-w-[280px]">{j.error}</span>}<StatusBadge status={j.status} testId="job-status-badge" /></div>
              </li>
            ))}
          </ul>
        )}
      </section>}
      {codesOpen && <CodesDialog rel={rel} open onClose={() => setCodesOpen(false)} onSaved={setRel} />}
    </div>
  );
}
